import { useCallback } from 'react';
import { navigate } from 'gatsby';
import { useRechargeStore } from '../store/recharge';
import { useSessionStore } from '../store/session';
import { useCustomerStore } from '../store/customer';
import { useChannelStore } from '../store/channel';
import { useUIStore } from '../store/ui';
import customerService from '../services/customerService';
import pixRechargeService from '../services/pixRechargeService';
import rechargeService from '../services/rechargeService';
import { sendUpsellPlanState } from '../services/timPostMessages';
import {
  trackAnalyticsUpsellControlAccept,
  trackAnalyticsUpsellControlDecline,
} from '../lib/tracking/events/recharge';
import { APP_CHANNELS } from '../constants/channels';
import type { SelectedCard, PixPaymentWindow } from '../types/recharge';
import { handleError } from '../lib/errors/handleError';

const MAX_POLL_ATTEMPTS = 5;
const POLL_DELAY_MS = 3000;
// Validade do Pix no gateway; só vale quando o pedido não traz a data real.
const PIX_VALIDITY_MS = 6 * 60 * 60 * 1000;
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const useRecharge = () => {
  const {
    targetMsisdn,
    setTargetMsisdn,
    rechargeValues,
    setRechargeValues,
    selectedRechargeValue,
    setSelectedRechargeValue,
    rechargeValueCampaign,
    setRechargeValueCampaign,
    rechargeResult,
    recurrenceResult,
    setRechargeResult,
    pixTransactionId,
    pixToken,
    pixRechargeValue,
    pixIntentionSent,
    isPixFastRecharge,
    pendingPixRecharge,
    pendingPixChecked,
    setPixTransactionId,
    setPixToken,
    setPixRechargeValue,
    setPixIntentionSent,
    setIsPixFastRecharge,
    setPendingPixRecharge,
    setPendingPixChecked,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    selectedCard,
    setSelectedCard,
    creditTransactionId,
    setCreditTransactionId,
  } = useRechargeStore();
  const { msisdn } = useSessionStore();
  const { setCustomer } = useCustomerStore();
  const { channel } = useChannelStore();
  const { showError, showLoading, hideLoading } = useUIStore();

  const addRecipient = useCallback(
    async (recipientMsisdn: string) => {
      showLoading();
      try {
        await customerService.addRecipient(msisdn, recipientMsisdn);
        const { data } = await customerService.getCustomer(msisdn);
        setCustomer(data);
      } catch (err) {
        handleError(err, showError);
        throw err;
      } finally {
        hideLoading();
      }
    },
    [msisdn, setCustomer, showError, showLoading, hideLoading],
  );

  const deleteRecipient = useCallback(
    async (recipientMsisdn: string) => {
      showLoading();
      try {
        await customerService.deleteRecipient(msisdn, recipientMsisdn);
        const { data } = await customerService.getCustomer(msisdn);
        setCustomer(data);
      } catch (err) {
        handleError(err, showError);
      } finally {
        hideLoading();
      }
    },
    [msisdn, setCustomer, showError, showLoading, hideLoading],
  );

  const getRechargeValues = useCallback(async () => {
    showLoading();
    try {
      const { data } =
        await customerService.getRechargeValuesWithAttributes(msisdn);
      setRechargeValues(data);
    } catch (err) {
      handleError(err, showError);
    } finally {
      hideLoading();
    }
  }, [msisdn, setRechargeValues, showError, showLoading, hideLoading]);

  const createPixRecharge = useCallback(
    async (rechargeValueId: number, recipientMsisdn: string | null) => {
      showLoading();
      try {
        const { data } = await pixRechargeService.createPixRecharge(
          msisdn,
          rechargeValueId,
          recipientMsisdn,
        );
        setPixTransactionId(data?.id ?? null);
        setPixRechargeValue(rechargeValueId);
        setIsPixFastRecharge(false);
        return data?.id ?? null;
      } catch (err) {
        handleError(err, showError);
        return null;
      } finally {
        hideLoading();
      }
    },
    [
      msisdn,
      setPixTransactionId,
      setPixRechargeValue,
      setIsPixFastRecharge,
      showError,
      showLoading,
      hideLoading,
    ],
  );

  const pollPixToken = useCallback(
    async (
      transactionId: string,
      attempts = 0,
    ): Promise<string | undefined> => {
      try {
        const { data } = await pixRechargeService.getPixToken(
          msisdn,
          transactionId,
        );
        if (data?.qr_code) {
          setPixToken(data.qr_code);
          return data.qr_code;
        }
      } catch (err) {
        if (err?.response?.status !== 404) throw err;
        // 404 = transação ainda não processada pelo backend — retenta
      }

      if (attempts >= MAX_POLL_ATTEMPTS - 1) {
        throw new Error('Timeout ao obter QR code PIX');
      }

      await delay(POLL_DELAY_MS);
      return pollPixToken(transactionId, attempts + 1);
    },
    [msisdn, setPixToken],
  );

  const sendRechargeIntention = useCallback(async () => {
    if (pixIntentionSent) return;
    setPixIntentionSent(true);
    try {
      await pixRechargeService.sendPixRechargeIntention(msisdn);
    } catch {
      // Falha silenciosa — intenção é analytics, não bloqueia o usuário
    }
  }, [msisdn, pixIntentionSent, setPixIntentionSent]);

  // Consulta o BFF por uma recarga Pix gerada e ainda não paga.
  // Roda só 1x por sessão (flag pendingPixChecked); nas reentradas na tela de
  // valores reaproveita o objeto já cacheado, evitando N requests. Silencioso:
  // 404 = sem pendência; qualquer outro erro não pode derrubar a tela.
  const checkPendingPixRecharge = useCallback(async () => {
    if (pendingPixChecked) {
      return useRechargeStore.getState().pendingPixRecharge;
    }
    setPendingPixChecked(true);
    try {
      const { data } = await pixRechargeService.getPendingPixRecharge(msisdn);
      setPendingPixRecharge(data ?? null);
      return data ?? null;
    } catch {
      setPendingPixRecharge(null);
      return null;
    }
  }, [msisdn, pendingPixChecked, setPendingPixChecked, setPendingPixRecharge]);

  // Validade do Pix recém-gerado, para o contador da tela de pagamento. O
  // polling do QR code só devolve o `qr_code`; a validade vem do pedido
  // aguardando pagamento, o mesmo que alimenta o Pix pendente. Não passa pelo
  // checkPendingPixRecharge: aquele grava `pendingPixRecharge`, que abre o
  // modal na valores-recarga. Sem o pedido (ainda não refletido, outro Pix,
  // erro), assume a janela padrão a partir de agora. Nunca rejeita.
  const getPixPaymentWindow = useCallback(
    async (transactionId: string): Promise<PixPaymentWindow> => {
      try {
        const { data } = await pixRechargeService.getPixOrder(msisdn);
        const { pixToken: currentPixToken } = useRechargeStore.getState();
        const isSamePix =
          !!data &&
          (data.correlationId === transactionId ||
            (!!currentPixToken && data.qr_code === currentPixToken));
        if (isSamePix && data.qr_code_expiration && data.createdAt) {
          return {
            expiration: data.qr_code_expiration,
            createdAt: data.createdAt,
          };
        }
      } catch {
        // 404 = pedido ainda não refletido; cai na janela padrão
      }
      const now = Date.now();
      return {
        expiration: new Date(now + PIX_VALIDITY_MS).toISOString(),
        createdAt: new Date(now).toISOString(),
      };
    },
    [msisdn],
  );

  // "Pagar com Pix" no modal: popula o contrato da tela de pagamento com os
  // dados do Pix pendente (rechargeResult + pixToken) e navega pra lá.
  //
  // `isPixFastRecharge` anda junto com `pixTransactionId`: esse campo destrava o
  // ramo de Pix do `confirmacao-recarga-avulsa`, que classifica a origem da
  // ordem por essa flag. A origem real de um Pix pendente é irrecuperável — a
  // flag mora na store em memória, que morre com a sessão que criou a ordem —,
  // então `false` aqui é o padrão, não uma afirmação sobre aquela recarga.
  const acceptPendingPixRecharge = useCallback(() => {
    const pending = useRechargeStore.getState().pendingPixRecharge;
    if (!pending) return;
    setRechargeResult({
      value: pending.value,
      recipient: pending.recipient,
      paymentType: pending.paymentType,
      expiration: pending.qr_code_expiration,
      createdAt: pending.createdAt,
    });
    setPixToken(pending.qr_code);
    setPixTransactionId(pending.correlationId ?? null);
    setIsPixFastRecharge(false);
    navigate('/web/pagamento-pix');
  }, [
    setRechargeResult,
    setPixToken,
    setPixTransactionId,
    setIsPixFastRecharge,
  ]);

  const sendCreditRecharge = useCallback(
    async ({
      targetMsisdn: recipient,
      card,
      rechargeValueId,
    }: {
      targetMsisdn: string | null;
      card: SelectedCard | null;
      rechargeValueId: number;
    }) => {
      showLoading();
      try {
        const { data } = await rechargeService.sendCreditRecharge(
          msisdn,
          recipient || msisdn,
          card,
          rechargeValueId,
        );
        setCreditTransactionId(data?.id ?? null);
        setPixTransactionId(null);
        navigate('/web/confirmacao-recarga-avulsa');
      } catch (err) {
        handleError(err, showError);
      } finally {
        hideLoading();
      }
      // O cartão pode ter sido adicionado ao perfil independente do resultado
      // da recarga — sempre atualiza o customer para refletir a lista de cartões
      try {
        const { data: customerData } =
          await customerService.getCustomer(msisdn);
        setCustomer(customerData);
      } catch {
        // falha silenciosa — não bloqueia o usuário
      }
    },
    [
      msisdn,
      setCreditTransactionId,
      setCustomer,
      showError,
      showLoading,
      hideLoading,
    ],
  );

  const sendPixFastRecharge = useCallback(
    async (rechargeValueId: number, recipient: string) => {
      showLoading();
      try {
        const { data } = await pixRechargeService.createPixRecharge(
          msisdn,
          rechargeValueId,
          recipient,
        );
        const transactionId = data?.id ?? null;
        setPixTransactionId(transactionId);
        setPixRechargeValue(rechargeValueId);
        setIsPixFastRecharge(true);
        if (transactionId) {
          navigate('/web/confirmacao-recarga-avulsa');
        }
      } catch (err) {
        handleError(err, showError);
        return null;
      } finally {
        hideLoading();
      }
    },
    [
      msisdn,
      setPixTransactionId,
      setPixRechargeValue,
      setIsPixFastRecharge,
      showError,
      showLoading,
      hideLoading,
    ],
  );

  const isPix = selectedPaymentMethod === 'pix';

  const getLatestRechargeValues = () =>
    useRechargeStore.getState().rechargeValues;

  // Estado (upsellPlan) e evento de tracking GA4/DM são iguais pra Web e App.
  // Só o Web abre a nova aba por conta própria — no App, quem navega é o
  // parent, reagindo ao action:"navigate" do próprio evento de tracking (por
  // isso o `shouldNavigate` do trackAnalyticsUpsellControlAccept só é true
  // nesse ramo). event-status do aceite no App é sempre "sucesso": não temos
  // retorno do App confirmando a navegação.
  const notifyUpsellControlAccepted = useCallback(
    (offer: { webLink: string; appLink: string }) => {
      const isAppChannel = (APP_CHANNELS as readonly string[]).includes(
        channel,
      );
      const link = isAppChannel ? offer.appLink : offer.webLink;

      sendUpsellPlanState({ upsellPlan: true, channel });

      if (isAppChannel) {
        trackAnalyticsUpsellControlAccept(link, true, 'sucesso');
        return;
      }

      const newTab = window.open(offer.webLink, '_blank');
      if (newTab) {
        trackAnalyticsUpsellControlAccept(link, false, 'sucesso');
      } else {
        trackAnalyticsUpsellControlAccept(
          link,
          false,
          'falha',
          'popup_blocked',
          'Navegador bloqueou a abertura da nova aba',
        );
      }
    },
    [channel],
  );

  const notifyUpsellControlDeclined = useCallback(() => {
    sendUpsellPlanState({ upsellPlan: false, channel });
    trackAnalyticsUpsellControlDecline();
  }, [channel]);

  return {
    targetMsisdn,
    setTargetMsisdn,
    rechargeValues,

    isPix,
    selectedRechargeValue,
    setSelectedRechargeValue,
    rechargeValueCampaign,
    setRechargeValueCampaign,
    rechargeResult,
    recurrenceResult,
    setRechargeResult,
    getLatestRechargeValues,
    pixTransactionId,
    pixToken,
    pixRechargeValue,
    setPixTransactionId,
    setPixRechargeValue,
    setCreditTransactionId,
    getRechargeValues,
    addRecipient,
    deleteRecipient,
    createPixRecharge,
    pollPixToken,
    getPixPaymentWindow,
    sendRechargeIntention,
    pendingPixRecharge,
    checkPendingPixRecharge,
    acceptPendingPixRecharge,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    selectedCard,
    setSelectedCard,
    creditTransactionId,
    sendCreditRecharge,
    sendPixFastRecharge,
    isPixFastRecharge,
    notifyUpsellControlAccepted,
    notifyUpsellControlDeclined,
  };
};
